import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import TrackTooltip from "../components/TrackTooltip";
import SetCard from "../components/Setlist/SetCard";
import ContextCard from "../components/Setlist/ContextCard";
import NotesCard from "../components/Setlist/NotesCard";
import { useTooltip } from "../hooks/useTooltip";
import "./SetlistPage.css";

function SetlistPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [setlist, setSetlistData] = useState(location.state?.setlist || null);
  const { loadPlaylist, playTrack, currentTrack, audioRef } = useAudioPlayer();
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const [collapsedSets, setCollapsedSets] = useState({});
  const [rawCollapsed, setRawCollapsed] = useState(true);

  const { tooltipState, handleInfoHover, closeTooltip } = useTooltip();

  // Load setlist from localStorage if not in route state
  useEffect(() => {
    if (!setlist) {
      const savedSetlist = localStorage.getItem("lastSetlist");
      if (savedSetlist) {
        try {
          setSetlistData(JSON.parse(savedSetlist));
        } catch (err) {
          console.error("Failed to parse saved setlist:", err);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (setlist?.playlist?.sections) {
      const allTracks = setlist.playlist.sections.flatMap(
        (section) => section.tracks
      );
      if (allTracks.length > 0) {
        const currentSrc = audioRef?.current?.src;
        const firstTrackUrl = allTracks[0]?.mp3_url;

        if (
          !currentSrc ||
          !firstTrackUrl ||
          !currentSrc.includes(firstTrackUrl.split("/").pop())
        ) {
          loadPlaylist(allTracks, allTracks[0]);
        }
      }

      localStorage.setItem("lastSetlist", JSON.stringify(setlist));
      localStorage.setItem("lastSetlistTimestamp", Date.now().toString());
    }
  }, [setlist, audioRef, loadPlaylist]);

  // Close tooltip on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeTooltip();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeTooltip]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tooltipState.visible &&
        !e.target.closest(".track-tooltip") &&
        !e.target.closest(".track-info-icon")
      ) {
        closeTooltip();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [tooltipState.visible, closeTooltip]);

  const handleTrackClick = (track, event) => {
    event.preventDefault();
    event.stopPropagation();
    playTrack(track);
  };

  const toggleSetCollapse = (setIndex) => {
    if (window.innerWidth <= 1024) {
      setCollapsedSets((prev) => ({
        ...prev,
        [setIndex]: !prev[setIndex],
      }));
    }
  };

  const handleTrackHover = (
    trackId,
    origin,
    event,
    isEnter,
    isClick = false
  ) => {
    handleInfoHover(trackId, origin, event, isEnter, isClick);
  };

  if (!setlist) {
    return (
      <div className="setlist-page">
        <main className="setlist-content">
          <div className="error-message">
            <h3>No Setlist Found</h3>
            <p>Please generate a setlist first.</p>
            <button onClick={() => navigate("/generate")} className="btn">
              Generate Show
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { sets, encore, metadata, playlist } = setlist;
  const allSets = encore ? [...sets, encore] : sets;
  const prettySetlist = JSON.stringify(setlist, null, 2);

  return (
    <div className="setlist-page">
      <main className="setlist-content">
        <div
          className={`setlist-display ${
            allSets.length >= 4 ? "four-cols" : ""
          }`}
        >
          {allSets.map((set, setIndex) => (
            <SetCard
              key={setIndex}
              set={set}
              setIndex={setIndex}
              isCollapsed={collapsedSets[setIndex]}
              onToggleCollapse={toggleSetCollapse}
              currentTrack={currentTrack}
              onTrackClick={handleTrackClick}
              tooltipState={tooltipState}
              onTrackHover={handleTrackHover}
            />
          ))}
        </div>

        <div className="bottom-row">
          <ContextCard
            metadata={metadata}
            playlist={playlist}
            seed={setlist?.seed}
            isCollapsed={contextCollapsed}
            onToggle={() => setContextCollapsed(!contextCollapsed)}
          />

          <NotesCard
            notes={metadata?.notes}
            isCollapsed={notesCollapsed}
            onToggle={() => setNotesCollapsed(!notesCollapsed)}
          />
        </div>

        {process.env.NODE_ENV === "development" && (
          <section
            className={`raw-json-card ${rawCollapsed ? "collapsed" : ""}`}
          >
            <div
              className="raw-json-header"
              role="button"
              tabIndex={0}
              onClick={() => setRawCollapsed((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRawCollapsed((prev) => !prev);
                }
              }}
            >
              <div className="raw-json-header-text">
                <h2>API Response</h2>
                <p>Read-only snapshot from the last /generate call.</p>
              </div>
              <span className="toggle-icon">{rawCollapsed ? "►" : "▼"}</span>
            </div>
            <div className="raw-json-content">
              <textarea
                className="raw-json-textarea"
                value={prettySetlist}
                readOnly
                spellCheck={false}
              />
            </div>
          </section>
        )}
      </main>

      <TrackTooltip
        trackId={tooltipState.trackId}
        origin={tooltipState.origin}
        position={tooltipState.position}
        visible={tooltipState.visible}
        onClose={closeTooltip}
      />
    </div>
  );
}

export default SetlistPage;
